<template lang="pug">
.reply-history-for-receive-request-tab
  .mb-3

  .reply-history-content.d-flex.flex-row.align-items-stretch
    .receive-message
      .content-title 受信内容
      .register-body.mt-2(v-if="replyTarget")
        .resident-info(v-if="_residentInfo")
          FormLabel(key-name="受信日時" :value="convertDatetime(replyTarget.datetime)")
          FormLabel(key-name="年齢" :value="_residentInfo.age")
          FormLabel(key-name="性別" :value="_residentInfo.gender")
          FormLabel(key-name="介護度" :value="convertCareLevelLabel(_residentInfo.careLevel)")
          FormLabel(key-name="生活保護の有無" :value="convertAcceptsWelfare(_residentInfo.welfare)")
          LabelAdl(v-model="_residentInfo.adl")
          FormLabel(key-name="退所（退院）予定日" :value="convertDate(_residentInfo.outDatetime)")
        FormLabel(key-name="メッセージ内容（補足説明等）" :value="replyTarget.messageText")
    .reply-messages.d-flex.flex-column
      .content-title 返信履歴
      .reply-mesasge-list(ref="replyMessageList")
        span.empty-reply(v-if="!_replies") 返信はありません。
        SendRequestReplyMessage.mb-2(v-else v-for="reply in _replies" :value="reply")
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { OtherOfficeAcceptanceRequestRepliesGetModel, OtherOfficeAcceptanceRequestReply } from "@/commons/services/OtherOfficeAcceptanceRequestReplies";
import { CareLevel, ResidentInfo } from "@/commons/services/OtherOfficeAcceptanceRequests";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import LabelAdl from "@/pc/components/otherOfficeRoomSchedules/LabelAdl.vue";

import { ReceiveRequestHistoriesTableItem } from "./ReceiveRequestHistoriesTable.vue";
import SendRequestReplyMessage from "@/pc/components/otherOfficeRoomSchedules/SendRequestReplyMessage.vue";

/**
 * ### 送信タブコンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    FormLabel,
    LabelAdl,
    SendRequestReplyMessage
  }
})
export default class ReplyHistoryForReceiveRequestTab extends Vue {
  /** 返信対象のリクエスト */
  @Prop()
  replyTarget!: ReceiveRequestHistoriesTableItem;
  /** リクエストに対する返信 */
  @Prop()
  replies!: OtherOfficeAcceptanceRequestRepliesGetModel;

  get _residentInfo(): ResidentInfo | undefined {
    return this.replyTarget.residentInfo;
  }

  get _replies(): Array<OtherOfficeAcceptanceRequestReply> | null {
    // 返信がない場合はnullを返す
    if (!this.replies.firstReply) {
      return null;
    }
    // 返信が1件以上あった場合、返信をまとめて返す
    else {
      return [this.replies.firstReply].concat(this.replies.followUpReplies ? this.replies.followUpReplies : []);
    }
  }

  /**
   * vue lifecycle mounted
   */
  mounted() {
    // タブ切り替えのアニメーション等で描画が完了していない場合があるため、少し待機してからスクロールする
    setTimeout(() => {
      this.scrollToBottom();
    }, 200);
    return;
  }

  @Watch("replies")
  onRepliesChanged() {
    // データが差し替わった場合に初期値を再設定する
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }

  /**
   * ### メッセージエリアを最下部にスクロールする
   */
  private scrollToBottom(): void {
    const element = this.$refs.replyMessageList as HTMLElement;
    if (element) {
      element.scrollTo({
        top: element.scrollHeight, // スクロール位置、末尾を指定して移動させる
        behavior: "smooth" // smoothを使用して、スクロールを滑らかに移動させる
      });
    }
    return;
  }

  convertDatetime(outDatetime: number | null): string {
    if (!outDatetime) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(outDatetime, "yyyy/mm/dd hh:MM");
  }

  convertCareLevelLabel(careLevel: CareLevel | null): string {
    if (!careLevel) {
      return "";
    }
    switch (careLevel) {
      case CareLevel.CARE_LEVEL_1000: {
        return "自立";
      }
      case CareLevel.CARE_LEVEL_1010: {
        return "申請中";
      }
      case CareLevel.CARE_LEVEL_2010: {
        return "要支援１";
      }
      case CareLevel.CARE_LEVEL_2020: {
        return "要支援２";
      }
      case CareLevel.CARE_LEVEL_3010: {
        return "要介護１";
      }
      case CareLevel.CARE_LEVEL_3020: {
        return "要介護２";
      }
      case CareLevel.CARE_LEVEL_3030: {
        return "要介護３";
      }
      case CareLevel.CARE_LEVEL_3040: {
        return "要介護４";
      }
      case CareLevel.CARE_LEVEL_3050: {
        return "要介護５";
      }
      default: {
        return "";
      }
    }
  }

  convertAcceptsWelfare(welfare: boolean | null): string {
    if (welfare == null) {
      return "";
    }
    if (welfare == true) {
      return "有り";
    } else {
      return "無し";
    }
  }

  convertDate(outDatetime: number | null): string {
    if (!outDatetime) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(outDatetime, "yyyy/mm/dd");
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.reply-history-for-receive-request-tab {
  @mixin title() {
    background-color: $bg-header;
    padding: 5px;
    min-height: 32px;
    font-weight: bold;
    text-align: center;
  }

  .reply-history-content {
    border: solid 1px $border-darkgray; // 外枠

    // 共通のコンテンツタイトル
    .content-title {
      @include title();
      border-bottom: solid 1px $border-lightgray;
      min-height: 35px;
    }

    // 左側 受信したリクエスト内容
    .receive-message {
      min-width: 50%;
      max-width: 50%;
    }
    // 右側 返信内容
    .reply-messages {
      min-width: 50%;
      max-width: 50%;
      border-left: solid 1px $border-darkgray; // 中央境界線

      // 返信履歴
      .reply-mesasge-list {
        max-height: 100%;
        overflow-y: auto;

        background-color: $bg-message;
        padding: 1rem;

        .empty-reply {
          font-weight: bold;
          text-align: center;
        }
      }
    }
  }
}
</style>
